/**
 * ValidatedStateContext
 * Manages validated field data and source metadata across page navigation.
 * Persists state to the session store (session.details) to survive navigation and browser refresh.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { FieldValue, FieldSource } from '../services/types';
import { SessionAuditClient } from '../services';

export interface ValidatedFormState {
  sessionId: string;
  batchId: string;
  lastValidatedAt: string;
  fields: Record<string, FieldValue>; // keyed by "tableKey.extractionKey"
  documentValidationCompleted: boolean;
}

interface ValidatedStateContextType {
  validatedState: ValidatedFormState | null;
  updateField: (fieldPath: string, fieldValue: FieldValue) => void;
  updateFields: (fields: Record<string, FieldValue>) => void;
  saveValidatedState: () => Promise<void>;
  loadValidatedState: () => Promise<void>;
  clearValidatedState: () => void;
  loading: boolean;
  error: string | null;
}

const ValidatedStateContext = createContext<ValidatedStateContextType | null>(null);

interface ValidatedStateProviderProps {
  children: React.ReactNode;
  sessionId: string | null;
  batchId: string;
  sessionClient: SessionAuditClient;
}

export const ValidatedStateProvider: React.FC<ValidatedStateProviderProps> = ({
  children,
  sessionId,
  batchId,
  sessionClient
}) => {
  const [validatedState, setValidatedState] = useState<ValidatedFormState | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const previousSessionIdRef = useRef<string | null>(null);

  // Clear validated state when sessionId changes (prevents stale data from previous BRR)
  useEffect(() => {
    if (previousSessionIdRef.current !== null && previousSessionIdRef.current !== sessionId) {
      setValidatedState(null);
      setError(null);
    }
    previousSessionIdRef.current = sessionId;
  }, [sessionId]);

  // Load validated state from the session store on mount
  const loadValidatedState = useCallback(async () => {
    if (!sessionId) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const session = await sessionClient.getSession(sessionId);

      if (!session) {
        setLoading(false);
        return;
      }

      const detailsColumn = sessionClient.columns.details;
      const detailsJson = session[detailsColumn];

      if (detailsJson) {
        try {
          const details = JSON.parse(detailsJson);
          if (details.validatedState) {
            setValidatedState(details.validatedState);
          }
        } catch (parseError) {
          console.error('[ValidatedState] Failed to parse session details:', parseError);
          setError('Failed to load validated state');
        }
      }
    } catch (err) {
      console.error('[ValidatedState] Failed to load validated state:', err);
      setError(err instanceof Error ? err.message : 'Failed to load validated state');
    } finally {
      setLoading(false);
    }
  }, [sessionId, sessionClient]);

  // Save validated state to the session store (debounced)
  const saveValidatedState = useCallback(async () => {
    if (!sessionId || !validatedState) {
      return;
    }

    try {
      // Get existing session to preserve other details
      const session = await sessionClient.getSession(sessionId);
      const detailsColumn = sessionClient.columns.details;

      let existingDetails = {};
      if (session && session[detailsColumn]) {
        try {
          existingDetails = JSON.parse(session[detailsColumn]);
        } catch (parseError) {
          console.warn('[ValidatedState] Could not parse existing details, starting fresh');
        }
      }

      // Merge validated state into details
      const updatedDetails = {
        ...existingDetails,
        validatedState: {
          ...validatedState,
          lastValidatedAt: new Date().toISOString()
        }
      };

      const detailsJson = JSON.stringify(updatedDetails);

      // Soft size guard: warn before the serialized details approach the backend field limit
      if (detailsJson.length > 90000) {
        console.warn('[ValidatedState] Validated state approaching size limit:', detailsJson.length, 'chars');
      }

      await sessionClient.updateSession(sessionId, {
        details: detailsJson,
        lastSavedAt: new Date()
      });
    } catch (err) {
      console.error('[ValidatedState] Failed to save validated state:', err);
      setError(err instanceof Error ? err.message : 'Failed to save validated state');
      throw err; // Re-throw so caller knows save failed
    }
  }, [sessionId, sessionClient, validatedState]);

  // Debounced save - wait 2 seconds after last update before saving
  const debouncedSave = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      void saveValidatedState();
    }, 2000);
  }, [saveValidatedState]);

  // Update a single field
  const updateField = useCallback((fieldPath: string, fieldValue: FieldValue) => {
    if (!sessionId) {
      console.warn('[ValidatedState] Cannot update field without sessionId');
      return;
    }

    setValidatedState(prev => {
      const newState: ValidatedFormState = prev || {
        sessionId,
        batchId,
        lastValidatedAt: new Date().toISOString(),
        fields: {},
        documentValidationCompleted: false
      };

      return {
        ...newState,
        fields: {
          ...newState.fields,
          [fieldPath]: fieldValue
        }
      };
    });

    // Note: We don't auto-save here to avoid excessive API calls
    // Caller should explicitly call saveValidatedState() when appropriate
  }, [sessionId, batchId]);

  // Update multiple fields at once (more efficient)
  const updateFields = useCallback((fields: Record<string, FieldValue>) => {
    if (!sessionId) {
      console.warn('[ValidatedState] Cannot update fields without sessionId');
      return;
    }

    setValidatedState(prev => {
      const newState: ValidatedFormState = prev || {
        sessionId,
        batchId,
        lastValidatedAt: new Date().toISOString(),
        fields: {},
        documentValidationCompleted: false
      };

      return {
        ...newState,
        fields: {
          ...newState.fields,
          ...fields
        }
      };
    });
  }, [sessionId, batchId]);

  // Clear validated state (e.g., when starting new BRR)
  const clearValidatedState = useCallback(() => {
    setValidatedState(null);
    setError(null);
  }, []);

  // Load validated state on mount when sessionId becomes available
  useEffect(() => {
    if (sessionId) {
      void loadValidatedState();
    }
  }, [sessionId, loadValidatedState]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const value: ValidatedStateContextType = {
    validatedState,
    updateField,
    updateFields,
    saveValidatedState,
    loadValidatedState,
    clearValidatedState,
    loading,
    error
  };

  return (
    <ValidatedStateContext.Provider value={value}>
      {children}
    </ValidatedStateContext.Provider>
  );
};

/**
 * Hook to access validated state context
 * Must be used within ValidatedStateProvider
 */
export const useValidatedState = (): ValidatedStateContextType => {
  const context = useContext(ValidatedStateContext);

  if (!context) {
    throw new Error('useValidatedState must be used within ValidatedStateProvider');
  }

  return context;
};
